import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, LayoutGrid, List as ListIcon, Search, Download, MapPin, X, Star, Loader2 } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Loading, EmptyState } from "@/components/common/Loading";
import { SeashellRating } from "@/components/common/Seashell";
import { useReferences, labelFor } from "@/hooks/useReferences";
import { useAuth } from "@/context/AuthContext";
import { MultiSelect } from "@/components/directory/MultiSelect";
import { GeoRadiusFilter, isRegionCenter } from "@/components/directory/GeoRadiusFilter";
import { DirectoryCreateButton } from "@/components/common/DirectoryCreateButton";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { useLocalMode } from "@/context/LocalModeContext";
import { trackResult } from "@/lib/analytics";
import { useAnalyticsImpressions } from "@/hooks/useAnalyticsImpressions";

const cityState = (l) => [l?.city, l?.state].filter(Boolean).join(", ");

function ResourceCard({ r, references, list, trackRef, onTrackClick }) {
  const place = cityState(r.location);
  return (
    <Link to={`/resource/${r.slug}`} data-testid={`resource-public-${r.id}`} ref={trackRef} onClick={onTrackClick}>
      <Card className={`border-0 transition-shadow hover:shadow-[0_8px_24px_rgba(16,24,40,0.08)] ${list ? "flex items-center gap-4 p-4" : "flex h-full flex-col overflow-hidden"}`}>
        {list ? (
          <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-secondary">
            {r.cover_url ? <img src={fileUrl(r.cover_url)} alt="" className="h-full w-full object-cover" /> : <BookOpen className="h-6 w-6 text-muted-foreground" />}
          </div>
        ) : (
          <div className="h-32 bg-gradient-to-r from-teal-100 to-teal-50">{r.cover_url && <img src={fileUrl(r.cover_url)} alt="" className="h-full w-full object-cover" />}</div>
        )}
        <div className={list ? "min-w-0 flex-1" : "flex flex-1 flex-col p-4"}>
          <div className="flex flex-wrap items-center gap-1.5">
            {r.featured && <Badge className="bg-amber-50 text-amber-700"><Star className="mr-1 h-3 w-3" /> Featured</Badge>}
            {r.resource_type && <Badge className="bg-teal-50 text-teal-700">{labelFor(references.resource_types, r.resource_type)}</Badge>}
            {r.has_file && <Badge variant="secondary"><Download className="mr-1 h-3 w-3" /> File</Badge>}
          </div>
          <h3 className="mt-2 line-clamp-2 font-[640] text-primary">{r.title}</h3>
          {r.author_name && <p className="mt-0.5 text-sm text-muted-foreground">by {r.author_name}</p>}
          {!list && r.summary && <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{r.summary}</p>}
          {place && <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> {place}</div>}
          <div className="mt-2"><SeashellRating value={r.rating_avg} readOnly size="h-4 w-4" showValue count={r.rating_count} /></div>
        </div>
      </Card>
    </Link>
  );
}

const RES_GROUPS = [
  { key: "resource_type", ref: "resource_types" },
  { key: "topics", ref: "resource_topics" },
  { key: "industries", ref: "industries" },
];

/**
 * The ONE Resources directory. `/resources` renders it unscoped; a Group's Resources tab
 * renders the SAME component with `groupId` set (GRP-DEC-317) — `group_id` goes to the
 * canonical `/resources/public` query and the server requires membership and pins the
 * result to that Group's approved associations.
 */
export function ResourcesDirectory({ groupId = null, groupName = null }) {
  const { references } = useReferences();
  const { user } = useAuth();
  const [view, setView] = useState("grid");
  const [source, setSource] = useState("all");
  const [sort, setSort] = useState("recent");
  const [multi, setMulti] = useState({ resource_type: [], topics: [], industries: [] });
  const [geo, setGeo] = useState({ geoOn: false, center: null, radius: 50 });
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");

  // Default the geo center to the signed-in user's saved location.
  useEffect(() => {
    let ok = true;
    if (user) {
      api.get("/directory/my-location").then(({ data }) => {
        if (ok && data && data.lat != null) {
          setGeo((g) => (g.center ? g : { ...g, center: { lat: data.lat, lng: data.lng, label: data.label || "My location" } }));
        }
      }).catch(() => {});
    }
    return () => { ok = false; };
  }, [user]);

  useEffect(() => { const t = setTimeout(() => setDq(q.trim()), 300); return () => clearTimeout(t); }, [q]);

  const setMultiKey = (key, arr) => setMulti((m) => ({ ...m, [key]: arr }));
  const patchGeo = (p) => setGeo((g) => ({ ...g, ...p }));

  const multiKey = JSON.stringify(multi);
  const localMode = useLocalMode();
  const centerKey = geo.center ? `${geo.center.lat},${geo.center.lng}` : "";

  const fetchPage = useCallback(async (offset, signal) => {
    const params = new URLSearchParams();
    if (multi.resource_type.length) params.set("resource_type", multi.resource_type.join(","));
    if (multi.topics.length) params.set("topics", multi.topics.join(","));
    if (multi.industries.length) params.set("industries", multi.industries.join(","));
    if (source !== "all") params.set("source", source);
    params.set("sort", sort);
    if (dq) params.set("q", dq);
    if (geo.geoOn && geo.center) {
      if (isRegionCenter(geo.center)) {
        if (geo.center.granularity === "state") params.set("state", geo.center.state_code);
        else params.set("country", geo.center.country_code);
      } else {
        params.set("lat", geo.center.lat);
        params.set("lng", geo.center.lng);
        params.set("radius", geo.radius);
      }
    }
    if (localMode.localParams) Object.entries(localMode.localParams).forEach(([k, v]) => params.set(k, String(v)));
    if (groupId) params.set("group_id", groupId);
    params.set("limit", "24");
    params.set("offset", String(offset));
    const { data } = await api.get(`/resources/public?${params.toString()}`, { signal });
    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, sort, dq, multiKey, geo.geoOn, geo.radius, centerKey, localMode.localKey, groupId]);

  const resetKey = `${source}|${sort}|${dq}|${multiKey}|${geo.geoOn}|${geo.radius}|${centerKey}|${localMode.localKey}|${groupId || ""}`;
  const { items, loadingMore, hasMore, sentinelRef } = useInfiniteScroll({ fetchPage, resetKey, pageSize: 24 });
  const bindImpression = useAnalyticsImpressions("directory_impression");
  const clickTracker = (r) => (r.analytics
    ? () => trackResult("directory_result_clicked", r.analytics) : undefined);

  const chips = useMemo(() => {
    const out = [];
    RES_GROUPS.forEach((g) => (multi[g.key] || []).forEach((v) => out.push({ key: g.key, value: v, label: labelFor(references[g.ref], v) })));
    if (geo.geoOn && geo.center) out.push({ key: "__geo", value: "geo", label: isRegionCenter(geo.center) ? `In ${geo.center.label}` : `Within ${geo.radius} mi of ${geo.center.label}` });
    return out;
  }, [multi, geo, references]);

  const removeChip = (chip) => {
    if (chip.key === "__geo") { patchGeo({ geoOn: false }); return; }
    setMultiKey(chip.key, (multi[chip.key] || []).filter((v) => v !== chip.value));
  };
  const clearAll = () => { setMulti({ resource_type: [], topics: [], industries: [] }); setSource("all"); patchGeo({ geoOn: false }); };
  const hasActive = chips.length > 0 || source !== "all";

  return (
    <div className={groupId ? "" : "mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8"}
         data-testid={groupId ? "group-resources-directory" : undefined}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          {groupId ? (
            <>
              <h2 className="text-base font-semibold text-foreground">Resources</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Resources {groupName ? `shared with ${groupName}` : "shared with this group"}.
                Members-only resources stay inside the group.
              </p>
            </>
          ) : (
            <>
              <h1 className="text-3xl font-[720] tracking-[-0.02em] text-foreground">Resources</h1>
              <p className="mt-1 text-muted-foreground">Books, tools, courses and guides curated by the community.</p>
            </>
          )}
        </div>
        <div className="flex items-center gap-3">
          <ToggleGroup type="single" value={view} onValueChange={(v) => v && setView(v)} className="rounded-lg border border-border p-1">
            <ToggleGroupItem value="grid" aria-label="Grid view" data-testid="view-grid"><LayoutGrid className="h-4 w-4" /></ToggleGroupItem>
            <ToggleGroupItem value="list" aria-label="List view" data-testid="view-list"><ListIcon className="h-4 w-4" /></ToggleGroupItem>
          </ToggleGroup>
          {groupId ? null : (
            <DirectoryCreateButton label="Add resource" to="/dashboard/resources?new=1" testId="resources-create-cta" />
          )}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <div className="min-w-[200px] flex-1">
          <Input placeholder="Search resources" value={q} onChange={(e) => setQ(e.target.value)} data-testid="resources-search" />
        </div>
        <div className="w-[140px]"><MultiSelect label="Any type" options={references.resource_types || []} selected={multi.resource_type} onChange={(a) => setMultiKey("resource_type", a)} testId="filter-type" /></div>
        <div className="w-[150px]"><MultiSelect label="Any topic" options={references.resource_topics || []} selected={multi.topics} onChange={(a) => setMultiKey("topics", a)} testId="filter-topic" /></div>
        <div className="w-[150px]"><MultiSelect label="Any industry" options={references.industries || []} selected={multi.industries} onChange={(a) => setMultiKey("industries", a)} testId="filter-industries" /></div>
        {false && (
          <Select value={source} onValueChange={setSource}><SelectTrigger className="w-[150px]" data-testid="filter-source"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Community &amp; Official</SelectItem><SelectItem value="community">Community</SelectItem><SelectItem value="internal">Official</SelectItem></SelectContent>
          </Select>
        )}
        <Select value={sort} onValueChange={setSort}><SelectTrigger className="w-[130px]" data-testid="filter-sort"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="recent">Most recent</SelectItem><SelectItem value="top_rated">Top rated</SelectItem></SelectContent>
        </Select>
        <GeoRadiusFilter value={geo} onChange={patchGeo} testId="resources-geo" />
      </div>

      {chips.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {chips.map((c, i) => (
            <button key={`${c.key}-${c.value}-${i}`} onClick={() => removeChip(c)} className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-3 py-1 text-xs font-[500] text-foreground transition-colors hover:bg-secondary" data-testid={`applied-chip-${c.value}`}>
              {c.label}<X className="h-3 w-3" />
            </button>
          ))}
          {hasActive && <button onClick={clearAll} className="text-xs font-[540] text-primary hover:underline" data-testid="resources-clear-filters">Clear all</button>}
        </div>
      )}

      {items !== null && (
        <div className="mt-4 text-sm text-muted-foreground" data-testid="resources-result-count">{items.length} result{items.length !== 1 ? "s" : ""}</div>
      )}

      <div className="mt-6">
        {items === null ? <Loading /> : items.length === 0 ? (
          <EmptyState icon={BookOpen} title="No resources found" description="Try adjusting your filters, or be the first to add one." testId="resources-empty" />
        ) : (
          <>
            {view === "grid" ? (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">{items.map((r) => <ResourceCard key={r.id} r={r} references={references} trackRef={bindImpression(r.analytics)} onTrackClick={clickTracker(r)} />)}</div>
            ) : (
              <div className="space-y-3">{items.map((r) => <ResourceCard key={r.id} r={r} references={references} list trackRef={bindImpression(r.analytics)} onTrackClick={clickTracker(r)} />)}</div>
            )}
            <div ref={sentinelRef} aria-hidden="true" className="h-1" />
            {loadingMore && <div className="mt-6 flex justify-center" data-testid="resources-loading-more"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
          </>
        )}
      </div>
    </div>
  );
}

export default function Resources() {
  return <ResourcesDirectory />;
}
